import React from 'react';
import { useForm } from 'react-hook-form';

// Helper to format date for datetime-local input (in local timezone)
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  // Format as YYYY-MM-DDTHH:MM in local timezone
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const MatchForm = ({ match, tournaments, teams, umpires = [], onSubmit, onCancel, isLoading }) => {
  const { register, handleSubmit, formState: { errors }, watch } = useForm({
    defaultValues: match ? {
      ...match,
      scheduled_date: formatDateForInput(match.scheduled_date),
      umpire1_id: match.umpires?.on_field_umpire_1 || '',
      umpire2_id: match.umpires?.on_field_umpire_2 || '',
      third_umpire_id: match.umpires?.third_umpire || '',
    } : {}
  });

  const selectedFormat = watch('format');
  const selectedTournamentId = watch('tournament_id');
  const selectedTournament = tournaments.find(t => t.id === selectedTournamentId);

  // Default overs by format for display hint
  const formatDefaults = {
    T10: { overs: 10, maxBowlerOvers: 2 },
    T20: { overs: 20, maxBowlerOvers: 4 },
    ODI: { overs: 50, maxBowlerOvers: 10 },
    TEST: { overs: null, maxBowlerOvers: null },
    CUSTOM: { overs: null, maxBowlerOvers: null },
  };

  // Get effective defaults (tournament custom or format default)
  const getEffectiveDefault = () => {
    if (selectedTournament?.overs_per_innings || selectedTournament?.max_overs_per_bowler) {
      return {
        overs: selectedTournament.overs_per_innings,
        maxBowlerOvers: selectedTournament.max_overs_per_bowler,
        source: 'tournament'
      };
    }
    if (selectedFormat && formatDefaults[selectedFormat]) {
      return {
        ...formatDefaults[selectedFormat],
        source: 'format'
      };
    }
    return null;
  };

  const effectiveDefault = getEffectiveDefault();

  // Transform form data before submit
  const handleFormSubmit = (data) => {
    const { umpire1_id, umpire2_id, third_umpire_id, ...rest } = data;

    // Build umpires object
    const umpiresObj = {};
    if (umpire1_id) umpiresObj.on_field_umpire_1 = umpire1_id;
    if (umpire2_id) umpiresObj.on_field_umpire_2 = umpire2_id;
    if (third_umpire_id) umpiresObj.third_umpire = third_umpire_id;

    // Convert local time to UTC ISO string for backend
    const scheduled_date_utc = rest.scheduled_date
      ? new Date(rest.scheduled_date).toISOString()
      : null;

    onSubmit({
      ...rest,
      scheduled_date: scheduled_date_utc,
      umpires: umpiresObj,
    });
  };

  const selectedTeams = watch(['team1_id', 'team2_id']);

  return (
    <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 border border-gray-200">
      <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">
        {match ? 'Edit Match' : 'Create New Match'}
      </h2>
      
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tournament *
            </label>
            <select
              {...register('tournament_id', { required: 'Tournament is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="">Select tournament</option>
              {tournaments.map(tournament => (
                <option key={tournament.id} value={tournament.id}>{tournament.name}</option>
              ))}
            </select>
            {errors.tournament_id && (
              <p className="text-red-500 text-sm mt-1">{errors.tournament_id.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Match Number *
            </label>
            <input
              {...register('match_number', { 
                required: 'Match number is required',
                min: { value: 1, message: 'Match number must be at least 1' }
              })}
              type="number"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
              placeholder="Enter match number"
            />
            {errors.match_number && (
              <p className="text-red-500 text-sm mt-1">{errors.match_number.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Team 1 *
            </label>
            <select
              {...register('team1_id', { required: 'Team 1 is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="">Select team 1</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
            {errors.team1_id && (
              <p className="text-red-500 text-sm mt-1">{errors.team1_id.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Team 2 *
            </label>
            <select
              {...register('team2_id', { 
                required: 'Team 2 is required',
                validate: value => value !== selectedTeams[0] || 'Team 2 must be different from Team 1'
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="">Select team 2</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
            {errors.team2_id && (
              <p className="text-red-500 text-sm mt-1">{errors.team2_id.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Format *
            </label>
            <select
              {...register('format', { required: 'Format is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="">Select format</option>
              <option value="T20">T20</option>
              <option value="ODI">ODI</option>
              <option value="TEST">TEST</option>
              <option value="T10">T10</option>
              <option value="CUSTOM">Custom</option>
            </select>
            {errors.format && (
              <p className="text-red-500 text-sm mt-1">{errors.format.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Venue *
            </label>
            <input
              {...register('venue', { required: 'Venue is required' })}
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
              placeholder="Enter venue name"
            />
            {errors.venue && (
              <p className="text-red-500 text-sm mt-1">{errors.venue.message}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Scheduled Date *
          </label>
          <input
            {...register('scheduled_date', { required: 'Scheduled date is required' })}
            type="datetime-local"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
          />
          {errors.scheduled_date && (
            <p className="text-red-500 text-sm mt-1">{errors.scheduled_date.message}</p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              On-field Umpire 1
            </label>
            <select
              {...register('umpire1_id')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="">Select umpire</option>
              {umpires.filter(u => u.umpire_type === 'on_field').map(umpire => (
                <option key={umpire.id} value={umpire.id}>{umpire.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              On-field Umpire 2
            </label>
            <select
              {...register('umpire2_id')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="">Select umpire</option>
              {umpires.filter(u => u.umpire_type === 'on_field').map(umpire => (
                <option key={umpire.id} value={umpire.id}>{umpire.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Third Umpire
            </label>
            <select
              {...register('third_umpire_id')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="">Select umpire</option>
              {umpires.filter(u => u.umpire_type === 'third_umpire').map(umpire => (
                <option key={umpire.id} value={umpire.id}>{umpire.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Custom Overs Configuration (Match-level override) */}
        <div className="border-t border-gray-200 pt-4 mt-4">
          <h3 className="text-md font-semibold text-gray-800 mb-3">
            Custom Overs Override
            <span className="text-sm font-normal text-gray-500 ml-2">(Optional)</span>
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Override overs for this specific match. Leave empty to use
            {effectiveDefault && (
              <span className="ml-1">
                {effectiveDefault.source === 'tournament' ? (
                  <span className="text-blue-600 font-medium">
                    tournament settings ({effectiveDefault.overs || 'Unlimited'} overs, {effectiveDefault.maxBowlerOvers || '∞'} overs/bowler)
                  </span>
                ) : (
                  <span>
                    format default ({effectiveDefault.overs || 'Unlimited'} overs, {effectiveDefault.maxBowlerOvers || '∞'} overs/bowler)
                  </span>
                )}
              </span>
            )}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Overs Per Innings
              </label>
              <input
                {...register('overs_per_innings', {
                  min: { value: 1, message: 'Minimum 1 over' },
                  max: { value: 90, message: 'Maximum 90 overs' }
                })}
                type="number"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
                placeholder={effectiveDefault ?
                  `Default: ${effectiveDefault.overs || 'Unlimited'}` :
                  'e.g., 15, 25, 30'}
              />
              {errors.overs_per_innings && (
                <p className="text-red-500 text-sm mt-1">{errors.overs_per_innings.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Overs Per Bowler
              </label>
              <input
                {...register('max_overs_per_bowler', {
                  min: { value: 1, message: 'Minimum 1 over' },
                  max: { value: 20, message: 'Maximum 20 overs' }
                })}
                type="number"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
                placeholder={effectiveDefault ?
                  `Default: ${effectiveDefault.maxBowlerOvers || 'Unlimited'}` :
                  'e.g., 3, 5, 6'}
              />
              {errors.max_overs_per_bowler && (
                <p className="text-red-500 text-sm mt-1">{errors.max_overs_per_bowler.message}</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3 sm:space-x-4 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="order-2 sm:order-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="order-1 sm:order-2 px-4 py-2 bg-cricket-green text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Saving...' : (match ? 'Update Match' : 'Create Match')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MatchForm;
